import { useState, useEffect, useRef } from 'react';
import { X, FileText, User, Building, DollarSign, CheckCircle } from 'lucide-react';
import ApplicationForm from './ApplicationForm';
import { apiService } from '../../services/api';
import { scrollToTop } from '../../hooks/useScrollToTop';

interface ApplicationModalProps {
  shop: any;
  onClose: () => void;
}

export default function ApplicationModal({ shop, onClose }: ApplicationModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Debug shop data
  console.log('ApplicationModal received shop:', shop);

  // Scroll to top when modal opens or step changes
  useEffect(() => {
    scrollToTop(modalRef.current);
  }, [currentStep]);

  // Scroll to top when modal first opens
  useEffect(() => {
    scrollToTop(modalRef.current);
  }, []);
  
  const [formData, setFormData] = useState({
    // Shop Information
    shop: shop?._id || shop?.id || '',
    shopNumber: shop?.shopNumber || '',
    shopSize: shop?.size || 0,
    shopFloor: shop?.floor || '',
    shopPurpose: shop?.purpose || '',
    Company: {
      CompanyName: '',
      PrincipalOfficeAddress: '',
      PhoneNumber: '',
      EmailAddress: '',
      EntityType: '',
      StateOfIncorporation: 'Zimbabwe',
      BusinessType: ''
    },
    LeaseGuarantee: {
      Guarantors: ['']
    },
    RentalHistory: {
      PresentAddress: '',
      PresentRent: '',
      PresentRentType: 'rent',
      PresentLandlordName: '',
      PresentLandlordPhone: '',
      PreviousAddress1: '',
      PreviousRent1: '',
      PreviousRentType1: 'rent',
      PreviousLandlordName1: '',
      PreviousLandlordPhone1: '',
      PreviousAddress2: ''
    },
    Financials: {
      GrossRevenue: '',
      TotalExpenses: ''
    },
    Consent: {
      ApplicantName: '',
      TenantSignatures: [
        { Signature: '', Date: new Date().toISOString().split('T')[0] },
        { Signature: '', Date: new Date().toISOString().split('T')[0] }
      ]
    },
    Images: []
  });

  const steps = [
    { id: 0, title: 'Shop Selection', icon: <Building className="h-5 w-5" /> },
    { id: 1, title: 'Your Company', icon: <Building className="h-5 w-5" /> },
    { id: 2, title: 'Guarantors', icon: <User className="h-5 w-5" /> },
    { id: 3, title: 'Rental History', icon: <FileText className="h-5 w-5" /> },
    { id: 4, title: 'Financial Info', icon: <DollarSign className="h-5 w-5" /> },
    { id: 5, title: 'Review & Sign', icon: <CheckCircle className="h-5 w-5" /> }
  ];

  const handleSubmit = async (data: any) => {
    try {
      // Clean up the data - remove empty strings and convert to proper types
      const cleanedData = {
        ...data,
        // Shop information
        shop: data.shop,
        shopNumber: data.shopNumber,
        shopSize: parseFloat(data.shopSize) || 0,
        shopFloor: data.shopFloor,
        shopPurpose: data.shopPurpose,
        // Clean email - remove spaces and normalize
        Company: {
          ...data.Company,
          EmailAddress: data.Company.EmailAddress.replace(/\s+/g, '').toLowerCase(),
          PhoneNumber: data.Company.PhoneNumber.replace(/\s+/g, ''),
        },
        // Convert string numbers to actual numbers
        Financials: {
          GrossRevenue: parseFloat(data.Financials.GrossRevenue) || 0,
          TotalExpenses: parseFloat(data.Financials.TotalExpenses) || 0
        },
        RentalHistory: {
          ...data.RentalHistory,
          PresentRent: parseFloat(data.RentalHistory.PresentRent) || 0,
          PreviousRent1: parseFloat(data.RentalHistory.PreviousRent1) || 0
        },
        // Clean up guarantors array
        LeaseGuarantee: {
          Guarantors: data.LeaseGuarantee.Guarantors.filter((g: string) => g.trim() !== '')
        }
      };
      
      console.log('Submitting lease application data:', cleanedData); // Debug log
      console.log('Shop data in cleanedData:', {
        shop: cleanedData.shop,
        shopNumber: cleanedData.shopNumber,
        shopSize: cleanedData.shopSize,
        shopFloor: cleanedData.shopFloor,
        shopPurpose: cleanedData.shopPurpose
      });
      
      // Validate required fields before sending
      const requiredFields = [
        'Company.CompanyName',
        'Company.EmailAddress',
        'Company.PhoneNumber',
        'Consent.ApplicantName'
      ];
      
      // Check shop fields separately since they should be pre-filled
      if (!cleanedData.shop || !cleanedData.shopNumber) {
        console.error('Shop data missing:', { shop: cleanedData.shop, shopNumber: cleanedData.shopNumber });
        alert('Shop information is missing. Please refresh the page and try again.');
        return;
      }
      
      const missingFields = requiredFields.filter(field => {
        if (field.includes('.')) {
          const [section, key] = field.split('.');
          return !cleanedData[section]?.[key] || cleanedData[section][key].toString().trim() === '';
        } else {
          // Handle top-level fields like 'shop', 'shopNumber'
          return !cleanedData[field] || cleanedData[field].toString().trim() === '';
        }
      });
      
      if (missingFields.length > 0) {
        alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }
      
      // Additional validation for phone number format
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,15}$/;
      if (!phoneRegex.test(cleanedData.Company.PhoneNumber.replace(/\s/g, ''))) {
        alert('Please enter a valid phone number (10-15 digits)');
        return;
      }
      
      // Additional validation for email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(cleanedData.Company.EmailAddress)) {
        alert('Please enter a valid email address');
        return;
      }
      
      // Submit application data to backend
      const result = await apiService.createLeaseApplication(cleanedData);

      if (result.status === 'success') {
        alert('Your lease application has been submitted successfully! We will review your details and get in touch if your application is shortlisted.');
        onClose();
      } else {
        alert(`Error: ${result.message || 'Failed to submit application'}`);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="application-modal-title">
      <div ref={modalRef} className="bg-white rounded-2xl shadow-soft-lg max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-brand-border shrink-0">
          <div className="flex-1 min-w-0">
            <h2 id="application-modal-title" className="text-lg sm:text-2xl font-bold text-brand-text">
              Commercial Leasing Application
            </h2>
            <p className="text-sm sm:text-base text-brand-muted mt-1">Shop {shop.shopNumber} – {shop.size} sqm</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-brand-muted hover:text-brand-text hover:bg-brand-surface transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-brand-surface border-b border-brand-border shrink-0">
          <div className="flex items-center justify-between overflow-x-auto">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-shrink-0">
                <div className={`flex items-center gap-1 sm:gap-2 ${
                  currentStep >= step.id ? 'text-primary' : 'text-gray-400'
                }`}>
                  <div className={`p-1.5 sm:p-2 rounded-full ${
                    currentStep >= step.id ? 'bg-primary text-white' : 'bg-gray-200'
                  }`}>
                    <div className="h-3 w-3 sm:h-4 sm:w-4">
                      {step.icon}
                    </div>
                  </div>
                  <span className="hidden sm:block font-medium text-xs sm:text-sm">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-6 sm:w-12 lg:w-24 h-0.5 mx-2 sm:mx-4 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
          <ApplicationForm
            shop={shop}
            currentStep={currentStep}
            formData={formData}
            setFormData={setFormData}
            setCurrentStep={setCurrentStep}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}